import {
  TeammatesQuery,
  User,
  UserOrderingInput,
  UserRoleEnum,
  UserOmissionAttributeEnum,
} from "@/__generated__/graphql";
import { useQuery } from "@apollo/client";
import { TEAMMATES_BY_ROLE } from "@/api/queries/teammates";

interface TeammateArgs {
  orgId?: string;
  roles: UserRoleEnum[];
  first?: number;
  last?: number;
  after?: string | null;
  before?: string | null;
  ordering: UserOrderingInput[];
  imageSize?: number;
  omissions?: UserOmissionAttributeEnum[];
}

export function useTeammates(variables: TeammateArgs) {
  const {
    orgId,
    imageSize,
    omissions = [
      UserOmissionAttributeEnum.Employee,
      UserOmissionAttributeEnum.FeaturedStudentOfficial,
      UserOmissionAttributeEnum.PermanentOrganizationOfficial,
    ],
    ...rest
  } = variables;

  const { data, loading, refetch } = useQuery(TEAMMATES_BY_ROLE, {
    variables: {
      orgId: orgId ?? "",
      imageSize: imageSize ?? 24,
      omissions,
      ...rest,
    },
    skip: !variables.orgId,
  });

  return { data: processData(data), loading, refetch };
}

interface UserData {
  users: User[];
  totalCount: number;
  hasPreviousPage?: boolean;
  hasNextPage?: boolean;
  startCursor?: string | null;
  endCursor?: string | null;
  cursor?: string | null;
}

function processData(data?: TeammatesQuery): UserData {
  if (data?.node?.__typename !== "Organization") {
    return { users: [], totalCount: 0 };
  }
  const users = data.node.users;
  const userData = users.edges
    ?.filter((edge) => edge?.__typename === "UserEdge")
    .map((edge) => {
      if (edge?.__typename === "UserEdge" && edge.node) {
        const { node } = edge;
        return {
          id: node.id ?? "",
          uuid: node.uuid ?? "",
          email: node.email,
          firstName: node.firstName ?? "",
          isDefaultCounselor: node.isDefaultCounselor,
          isSuperCounselor: node.isSuperCounselor,
          lastActiveAt: node.lastActiveAt
            ? new Date(node.lastActiveAt * 1000)
            : undefined,
          lastName: node.lastName ?? "",
          name: node.name ?? "",
          phoneNumber: node.phoneNumber,
          profilePhoto: node.profilePhoto,
          role: node.role,
        };
      }
    })
    .filter((map) => !!map);
  return {
    users: userData ?? [],
    totalCount: users?.totalCount ?? 0,
    hasPreviousPage: users?.pageInfo?.hasPreviousPage,
    hasNextPage: users?.pageInfo?.hasNextPage,
    startCursor: users?.pageInfo?.startCursor,
    endCursor: users?.pageInfo?.endCursor,
    cursor: users?.pageInfo?.endCursor,
  };
}
